import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

export default function EmailPopup({
  dropDownEmails = [],
  HeaderText = "",
  BodyText = "",
  SubmitFunction = () => {},
  cancelFunction = () => {},
  onHide = () => {},
  show = false,
  SubmitText = "Submit",
  CancelText = "Cancel",
}) {
  const [emailInputs, setEmailInputs] = useState([""]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState({ 0: false });
  const inputRefs = useRef([]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        handleCancel();
      }
    };

    if (show) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [show]);

  // Reset state when popup opens
  useEffect(() => {
    if (show) {
      setEmailInputs([""]);
      setSelectedEmails([]);
      setDropdownVisible({ 0: false });
    }
  }, [show]);

  const handleEmailInputChange = (index, value) => {
    const newEmailInputs = [...emailInputs];
    const oldValue = newEmailInputs[index];
    newEmailInputs[index] = value;
    setEmailInputs(newEmailInputs);

    // Remove old email from selected list if it was changed
    if (oldValue && oldValue !== value && selectedEmails.includes(oldValue)) {
      setSelectedEmails((prev) => prev.filter((email) => email !== oldValue));
    }

    // Show dropdown if there are available emails
    if (dropDownEmails.length > 0) {
      const filteredEmails = getFilteredDropdownEmails(value, index);

      setDropdownVisible((prev) => ({
        ...prev,
        [index]: filteredEmails.length > 0,
      }));
    } else {
      setDropdownVisible((prev) => ({
        ...prev,
        [index]: false,
      }));
    }
  };

  const handleEmailInputBlur = (index, value) => {
    // Only add to selected emails when user finishes typing (on blur)
    if (
      value.trim() &&
      isValidEmail(value) &&
      !selectedEmails.includes(value)
    ) {
      console.log("Adding email to selected on blur:", value);
      setSelectedEmails((prev) => [...prev, value]);
    }
  };

  const handleDropdownSelect = (index, email) => {
    console.log("Dropdown selection:", email);
    const newEmailInputs = [...emailInputs];
    newEmailInputs[index] = email;
    setEmailInputs(newEmailInputs);

    // Hide dropdown
    setDropdownVisible((prev) => ({
      ...prev,
      [index]: false,
    }));

    // Add to selected emails if not already present
    if (!selectedEmails.includes(email)) {
      console.log("Adding email to selected from dropdown:", email);
      setSelectedEmails([...selectedEmails, email]);
    }
  };

  const addEmailInput = () => {
    if (emailInputs.length < 5) {
      const newLength = emailInputs.length;
      setEmailInputs([...emailInputs, ""]);
      // Initialize dropdown state for the new input
      setDropdownVisible((prev) => ({
        ...prev,
        [newLength]: false,
      }));
    }
  };

  const removeEmailInput = (index) => {
    if (emailInputs.length > 1) {
      const newEmailInputs = emailInputs.filter((_, i) => i !== index);
      setEmailInputs(newEmailInputs);

      // Remove from selected emails if it exists
      const emailToRemove = emailInputs[index];
      if (emailToRemove && selectedEmails.includes(emailToRemove)) {
        setSelectedEmails(
          selectedEmails.filter((email) => email !== emailToRemove)
        );
      }
    }
  };

  const handleSubmit = () => {
    // Collect all valid emails from both selectedEmails and current inputs
    const allValidEmails = [...selectedEmails];

    // Add any valid emails from inputs that aren't already in selectedEmails
    emailInputs.forEach((email) => {
      if (
        email.trim() &&
        isValidEmail(email) &&
        !allValidEmails.includes(email)
      ) {
        allValidEmails.push(email);
      }
    });

    const uniqueEmails = [...new Set(allValidEmails)];
    SubmitFunction({ emails: uniqueEmails });
    onHide();
  };

  const handleCancel = () => {
    cancelFunction();
    onHide();
  };

  const getFilteredDropdownEmails = (inputValue, index) => {
    // If no input value, show all available emails
    if (!inputValue.trim()) {
      return dropDownEmails.filter((email) => !selectedEmails.includes(email));
    }

    return dropDownEmails.filter(
      (email) =>
        email.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedEmails.includes(email)
    );
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const hasValidEmailsToSubmit = () => {
    // Check if we have any selected emails or any valid emails in inputs
    if (selectedEmails.length > 0) return true;

    return emailInputs.some((email) => email.trim() && isValidEmail(email));
  };

  return (
    <Dialog open={show} onClose={handleCancel} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel className="relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={handleCancel}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <div className="sm:flex sm:items-start">
              <div className="w-full mt-3 text-center sm:ml-0 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900 mb-4"
                >
                  {HeaderText}
                </DialogTitle>

                {BodyText && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500">{BodyText}</p>
                  </div>
                )}

                <div className="space-y-3 mb-6">
                  {emailInputs.map((email, index) => (
                    <div key={index} className="relative">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 relative">
                          <input
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="email"
                            value={email}
                            onChange={(e) =>
                              handleEmailInputChange(index, e.target.value)
                            }
                            placeholder="Enter email address"
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              email && !isValidEmail(email)
                                ? "border-red-300 text-red-900"
                                : "border-gray-300"
                            }`}
                            onFocus={() => {
                              // Show dropdown on focus if there are available emails
                              if (dropDownEmails.length > 0) {
                                const filteredEmails =
                                  getFilteredDropdownEmails(email, index);

                                setDropdownVisible((prev) => ({
                                  ...prev,
                                  [index]: filteredEmails.length > 0,
                                }));
                              }
                            }}
                            onBlur={() => {
                              // Add valid email to selected list when user finishes typing
                              handleEmailInputBlur(index, email);

                              // Delay hiding dropdown to allow for clicks
                              setTimeout(() => {
                                setDropdownVisible((prev) => ({
                                  ...prev,
                                  [index]: false,
                                }));
                              }, 200);
                            }}
                          />

                          {/* Dropdown */}
                          {(() => {
                            return dropdownVisible[index];
                          })() && (
                            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-32 overflow-y-auto">
                              {getFilteredDropdownEmails(email, index).map(
                                (dropdownEmail, emailIndex) => (
                                  <button
                                    key={emailIndex}
                                    type="button"
                                    className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                                    onClick={() =>
                                      handleDropdownSelect(index, dropdownEmail)
                                    }
                                  >
                                    {dropdownEmail}
                                  </button>
                                )
                              )}
                            </div>
                          )}
                        </div>

                        {emailInputs.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEmailInput(index)}
                            className="text-red-500 hover:text-red-700 focus:outline-none"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>

                      {email && !isValidEmail(email) && (
                        <p className="mt-1 text-sm text-red-600">
                          Please enter a valid email address
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add another email link */}
                {emailInputs.length < 5 && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={addEmailInput}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none"
                    >
                      + Add another email
                    </button>
                  </div>
                )}

                {selectedEmails.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Selected Emails:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedEmails.map((email, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 hover:cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
                disabled={!hasValidEmailsToSubmit()}
              >
                {SubmitText}
              </button>
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                onClick={handleCancel}
              >
                {CancelText}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
